import { forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';
import { PaymentMethodsApiAdapter } from '../../../adapters/PaymentMethodsApiAdapter';
import { PaymentMethodErrorApiAdapter } from '../../../adapters/PaymentMethodErrorApiAdapter';
import { createHttpClient } from '@shared/adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useToast } from '@shared/hooks/use-toast';
import { translateOrderApiError } from '../../../../CreateEditOrders/utils/errorTranslator';

export interface PaymentMethodErrorModalRef {
  openModal: (orderId: number) => void;
  closeModal: () => void;
}

interface PaymentMethodErrorModalProps {
  onSuccess?: () => void;
}

export const PaymentMethodErrorModal = forwardRef<PaymentMethodErrorModalRef, PaymentMethodErrorModalProps>(
  ({ onSuccess }, ref) => {
    const { t } = useTranslation('viewOrdersModule');
    const { toast } = useToast();
    const modalRef = useRef<ModalCenterRef>(null);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
    const [paymentMethods, setPaymentMethods] = useState<Array<{ label: string; value: string }>>([]);
    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentOrderId, setCurrentOrderId] = useState<number | null>(null);

    useImperativeHandle(ref, () => ({
      openModal: (orderId: number) => {
        setCurrentOrderId(orderId);
        modalRef.current?.openModal();
      },
      closeModal: () => modalRef.current?.closeModal(),
    }));

    useEffect(() => {
      const loadPaymentMethods = async () => {
        setLoading(true);
        try {
          const httpClient = createHttpClient(apiClientWH);
          const adapter = new PaymentMethodsApiAdapter(httpClient);
          const methods = await adapter.getAll();

          const options = methods.map((method: any) => ({
            label: method.name,
            value: method.id,
          }));
          setPaymentMethods(options);
        } catch (error) {
          console.error('Error loading payment methods:', error);
        } finally {
          setLoading(false);
        }
      };

      loadPaymentMethods();
    }, []);

    const handleClose = () => {
      modalRef.current?.closeModal();
    };

    const handleBack = () => {
      modalRef.current?.closeModal();
    };

    const handleAdd = async () => {
      if (!currentOrderId) {
        toast({
          title: t('paymentMethod.error.title'),
          description: 'ID del pedido no encontrado',
          variant: 'destructive',
        });
        return;
      }

      if (!selectedPaymentMethod) {
        toast({
          title: t('paymentMethod.error.title'),
          description: 'Debes seleccionar un método de pago',
          variant: 'destructive',
        });
        return;
      }

      setIsSubmitting(true);

      try {
        const httpClient = createHttpClient(apiClientWH);
        const adapter = new PaymentMethodErrorApiAdapter(httpClient);

        console.log('🔍 PaymentMethodErrorModal - Sending data:', {
          orderId: currentOrderId,
          payment_method_id: parseInt(selectedPaymentMethod),
        });

        const response = await adapter.solvePaymentMethodError(currentOrderId, {
          payment_method_id: parseInt(selectedPaymentMethod),
        });

        console.log('🔍 PaymentMethodErrorModal - Response:', response);

        if (response.success) {
          toast({
            title: t('paymentMethod.success.title'),
            description: response.message,
            variant: 'default',
          });

          // Ejecutar callback de éxito si existe
          onSuccess?.();
        } else {
          toast({
            title: t('paymentMethod.error.title'),
            description: response.message,
            variant: 'destructive',
          });
        }
      } catch (error: any) {
        // Traducir el error usando el sistema de traducción
        const translatedError = translateOrderApiError(error, t);

        toast({
          title: t('paymentMethod.error.title'),
          description: translatedError,
          variant: 'destructive',
        });
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <ModalCenter
        ref={modalRef}
        title={t('paymentMethod.title')}
        icon={
          <button
            onClick={handleBack}
            className="flex items-center justify-center w-6 h-6 hover:bg-hover-press rounded transition-colors"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
          </button>
        }
        type="default"
        fullScreenMobile={true}
        width="lg"
        className="max-h-[90vh]"
      >
        <div className="flex flex-col h-full">
          {/* Contenido principal */}
          <div className="flex-1 space-y-4 mt-2 pb-24 md:pb-0 max-h-[70vh] overflow-y-auto">
            <p className="text-sm">{t('paymentMethod.description')}</p>

            <div className="space-y-2">
              <label className="text-title font-medium">{t('paymentMethod.label')}</label>
              <Combobox
                options={paymentMethods}
                value={selectedPaymentMethod}
                setValue={setSelectedPaymentMethod}
                placeholder={t('paymentMethod.placeholder')}
                disabled={loading}
              />
            </div>
          </div>

          {/* Botones de acción - Fijos en mobile */}
          <div className="fixed bottom-0 left-0 right-0 p-4 md:pb-0 md:px-0 md:relative md:border-t-0">
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={handleClose} size="lg">
                {t('paymentMethod.cancel')}
              </Button>
              <Button variant="primary" onClick={handleAdd} disabled={!selectedPaymentMethod || isSubmitting} size="lg">
                {isSubmitting ? t('paymentMethod.submitting') : t('paymentMethod.add')}
              </Button>
            </div>
          </div>
        </div>
      </ModalCenter>
    );
  },
);
